import { useEffect, useContext, useState } from 'react'

// Components
import PageWrapper from 'components/PageWrapper'

// Api
import OTPValidation from 'components/OTPValidation'
import { finishAdvance } from 'api/advances'
import { createSubscriber } from 'api/memberships'
import { ADVANCE_TERMS_SLUG, getTerms } from 'api/term-conditions'
import { acceptAdvanceTerms } from 'api/productsTerms'

// context + store
import { MeContext } from 'contexts/me'
import { useAdvanceSuccess, useCommercialOffer } from 'store/commercialOffer'

import { hasActiveMembership } from 'utils'
import { trackEvent } from 'analytics/trackFunctions'
import { useNavigate } from 'react-router-dom'
import { CREDIT_TYPES, ATTEMPT_STATUS } from 'constants/credit'
import { SUBORIGIN_OPTIONS } from 'constants/membership'

type EventProps = {
  monto_solicitado: number
  monto_linea_credito: number
  vencimiento_de_la_cuota: string
  fecha_de_otorgamiento: string
  monto_a_devolver: number
  cantidad_adelantos_previos: number
}

const { SUBMITTED, SUBMITTED_WITH_DELAYS, REJECTED } = ATTEMPT_STATUS

const AdvanceOTPValidation = () => {
  const [OtpInput, setOtpInput] = useState(new Array(6).fill(''))
  const [isRequesting, setIsRequesting] = useState(false)
  const [termsID, setTermsID] = useState(null)

  const [inputStatus, setInputStatus] = useState('waiting')

  const navigate = useNavigate()
  const { me } = useContext(MeContext)

  const { updateAdvanceData, updateIsDelayed } = useAdvanceSuccess()

  const {
    creditLine,
    isMembershipSelected: membershipSwitch,
    selectedOffer: { membership: selectedAdvance, creditOptionId, advanceType }
  } = useCommercialOffer()

  const advanceRequest = (eventProps: EventProps) => {
    // delete possible cached data
    updateIsDelayed(false)
    // accept advance terms
    acceptAdvanceTerms({ identifier: termsID })
      .then(() =>
        finishAdvance({
          credit_option_id: creditOptionId,
          credit_option_plan_id: selectedAdvance.id,
          wallet_account: me.bank.internal_account[0].wallet_account,
          otp_code: Number(OtpInput.join('')),
          loan_type: advanceType
        })
      )
      .then((res) => handleFinishAdvanceResponse(res, eventProps))
      .catch((error) => handleFinishAdvanceError(error, eventProps))
      .finally(() => setIsRequesting(false))
  }

  const handleFinishAdvanceResponse = (res: any, eventProps: EventProps) => {
    if (res.success) {
      updateAdvanceData(
        selectedAdvance.delivered_amount,
        !hasActiveMembership(me?.subscriber_products) && membershipSwitch,
        advanceType
      )
      trackEvent({
        eventName: 'bille_adelantos_oferta_confirmar_exito',
        props: eventProps
      })

      setInputStatus('success')

      if (res.data.status === REJECTED) {
        return navigate('/error-adelanto')
      }

      if (res.data.status === SUBMITTED_WITH_DELAYS) {
        updateIsDelayed(true)
      }

      setTimeout(() => {
        navigate('/fin-adelanto', { replace: true })
      }, 5000)
    } else {
      trackEvent({
        eventName: 'bille_adelantos_oferta_confirmar_error',
        props: {
          ...eventProps,
          error: JSON.stringify(res)
        }
      })
      setInputStatus('error')
      setOtpInput(new Array(6).fill(''))
    }
  }

  const handleFinishAdvanceError = (error: any, eventProps: EventProps) => {
    trackEvent({
      eventName: 'bille_adelantos_oferta_error',
      props: {
        ...eventProps,
        error: JSON.stringify(error)
      }
    })
    navigate('/error-adelanto')
  }

  const handleConfirm = () => {
    if (isRequesting) return

    setIsRequesting(true)

    const eventProps = {
      monto_solicitado: selectedAdvance.delivered_amount,
      monto_linea_credito: creditLine.maxAmount,
      vencimiento_de_la_cuota: selectedAdvance.due_date,
      fecha_de_otorgamiento: selectedAdvance.start_date,
      monto_a_devolver: selectedAdvance.repayable_amount,
      cantidad_adelantos_previos: creditLine.advanceCounter
    }

    trackEvent({
      eventName: 'bille_adelantos_boton_oferta_confirmar',
      props: eventProps
    })

    if (membershipSwitch && !hasActiveMembership(me?.subscriber_products)) {
      const subscriberData = {
        name: me.profile.name,
        surname: me.profile.surname,
        email: me.profile.email,
        identificationNumber: me.profile.identification_number,
        phone: me.profile.phone,
        userUuid: me.user.uuid,
        subOrigin:
          advanceType === CREDIT_TYPES.ZERO_RATE
            ? SUBORIGIN_OPTIONS.INTEREST_FREE_ADVANCE
            : SUBORIGIN_OPTIONS.CASH_ADVANCE
      }

      trackEvent({
        eventName:
          'bille_adelantos_oferta_solicitar_boton_suscripcion_wayniplus',
        props: eventProps
      })

      createSubscriber(subscriberData)
        .then((res) => {
          if (!res.success || res.error) {
            navigate('/error-adelanto')
          } else {
            setTimeout(() => {
              advanceRequest(eventProps)
            }, 2000)
          }
        })
        .catch(() => navigate('/error-adelanto'))
    } else {
      advanceRequest(eventProps)
    }
  }

  useEffect(() => {
    trackEvent({
      eventName: 'bille_adelantos_pantalla_otp'
    })

    getTerms(ADVANCE_TERMS_SLUG).then((res) => {
      setTermsID(res?.identifier)
    })
  }, [])

  return (
    <PageWrapper viewSelected='/wayni-credito' title='Pedir adelanto' noTabBar>
      <section className='sm:max-w-564 flex flex-col items-center sm:mt-0 mx-auto'>
        <OTPValidation
          inputValue={OtpInput}
          setInputValue={setOtpInput}
          confirmAction={handleConfirm}
          inputStatus={inputStatus}
          isRequesting={isRequesting}
        />
      </section>
    </PageWrapper>
  )
}

export default AdvanceOTPValidation
